import { browserSessionPersistence } from '@angular/fire/auth';
import { initializeApp, getApps, getApp } from 'firebase/app';
import {
    getAuth,
    signInWithPopup,
    OAuthProvider,
    signOut,
    onAuthStateChanged,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    sendPasswordResetEmail,
    setPersistence,
    browserLocalPersistence,
    Auth
} from 'firebase/auth';

class FirebaseAuthBackend {
    private auth: Auth;

    constructor(firebaseConfig: any) {
        if (firebaseConfig) {
            // Wait for Angular Fire to initialize the app first
            const apps = getApps();

            if (apps.length === 0) {
                console.warn('⚠️  Firebase app not initialized yet. Angular Fire should initialize it first.');
                console.warn('This might cause duplicate app instances. Initializing anyway...');
                initializeApp(firebaseConfig);
            } else {
                console.log('✓ Found existing Firebase app. Using shared instance.');
            }

            // Get the default app and its Auth instance
            // This will be the SAME Auth instance that Angular Fire uses
            const app = getApp();
            this.auth = getAuth(app);

            console.log('Auth instance info:', {
                app: app.name,
                authInstance: this.auth,
                currentUser: this.auth.currentUser
            });

            // Set persistence to sessionStorage for no persistent auth across browser sessions
            setPersistence(this.auth, browserLocalPersistence)
                .then(() => {
                    console.log('Firebase Auth persistence set to localStorage');
                })
                .catch((error) => {
                    console.error('Error setting Firebase Auth persistence:', error);
                });

            console.log('Setting up onAuthStateChanged listener...');
            onAuthStateChanged(this.auth, (user) => {
                console.log('🔔 onAuthStateChanged fired! User:', user ? user.uid : 'null');
                // State is now managed by @angular/fire observables and auth.service.ts
            });
        }
    }

    /**
     * Registers the user with given details
     */
    registerUser = (email: string, password: string) => {
        return new Promise((resolve, reject) => {
            createUserWithEmailAndPassword(this.auth, email, password)
                .then((userCredential: any) => {
                    const user = userCredential.user;
                    resolve(user);
                })
                .catch((error) => {
                    reject(this._handleError(error));
                });
        });
    }

    /**
     * Login user with given details
     */
    loginUser = (email: string, password: string) => {
        return new Promise((resolve, reject) => {
            signInWithEmailAndPassword(this.auth, email, password)
                .then((userCredential: any) => {
                    const user = userCredential.user;
                    resolve(user);
                })
                .catch((error) => {
                    reject(this._handleError(error));
                });
        });
    }

    /**
     * forget Password user with given details
     */
    forgetPassword = (email: string) => {
        return new Promise((resolve, reject) => {
            sendPasswordResetEmail(this.auth, email, { url: window.location.protocol + '//' + window.location.host + '/login' })
                .then(() => {
                    resolve(true);
                })
                .catch((error) => {
                    reject(this._handleError(error));
                });
        });
    }

    /**
     * Logout the user
     */
    logout = () => {
        return new Promise((resolve, reject) => {
            signOut(this.auth)
                .then(() => {
                    // OIDC tokens now cleared by AuthenticationService in-memory
                    resolve(true);
                })
                .catch((error) => {
                    reject(this._handleError(error));
                });
        });
    }

    /**
     * Handle the error
     * @param {*} error
     */
    _handleError(error) {
        // tslint:disable-next-line: prefer-const
        var errorMessage = error.message;
        return errorMessage;
    }
}

// tslint:disable-next-line: variable-name
// Typed explicitly: inferred as `null`, every caller became `never`.
let _fireBaseBackend: FirebaseAuthBackend | null = null;

/**
 * Initialize the backend
 * @param {*} config
 */
const initFirebaseBackend = (config) => {
    if (!_fireBaseBackend) {
        _fireBaseBackend = new FirebaseAuthBackend(config);
    }
    return _fireBaseBackend;
};

/**
 * Returns the firebase backend
 */
const getFirebaseBackend = () => {
    return _fireBaseBackend;
};

export { initFirebaseBackend, getFirebaseBackend };
